import { Box, Flex, Text } from '@chakra-ui/react';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Banner, Button, ButtonText, useToast } from '@morressier/alembic';
import {
  ISubmissionContextConfig,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import {
  FormFieldFileUpload,
  FormFieldSubmittingAuthor
} from '@publishing-engine/domain/types/article';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSubmittingAuthorOnlySubmitICMJEForm } from 'src/api/commands';
import { useKeycloak } from 'src/components/AuthHOC';
import { FormFieldSingleFileUpload } from 'src/components/FormField';
import ProtectedAction from 'src/components/ProtectedAction';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { downloadFile } from 'src/utils/file';

type Props = {
  config: ISubmissionContextConfig;
};

type ISubmittingAuthorOnlyICMJEForm = {
  authorICMJEForm: {
    file: ISubmissionFile;
  };
};

const SubmittingAuthorOnlyICMJEForm: React.FC<Props> = ({ config }) => {
  const {
    submissionId,
    workflowResponse,
    submissionContextId,
    organizationId,
    fieldsResponse,
    refetchSubmission
  } = useSubmissionData();

  const { getFieldState } = useSubmissionFields();
  const { currentUser } = useKeycloak();
  const { articleWorkflowId } = fieldsResponse;
  const toast = useToast();
  const authorUploadICMJESubmissionForm =
    workflowResponse.forms.authorUploadICMJE;

  if (!authorUploadICMJESubmissionForm) {
    return;
  }

  const icmjeFormFields = new GenericFormFields(
    authorUploadICMJESubmissionForm
  );

  const submittingAuthorField =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')!;

  const configs = {
    AuthorICMJEFormFile: icmjeFormFields.getFieldConfig(
      'AuthorICMJEFormFile'
    ) as FormFieldFileUpload
  };

  const methods = useForm<ISubmittingAuthorOnlyICMJEForm>();

  const { handleSubmit, getValues } = methods;

  const submittingAuthorOnlyICMJESubmissionMutation =
    useSubmittingAuthorOnlySubmitICMJEForm({
      submissionId,
      organizationId,
      submissionContextId,
      articleWorkflowId
    });

  const { isLoading: isSubmitting } =
    submittingAuthorOnlyICMJESubmissionMutation;

  const { authorICMJEForm } = getValues();
  const isDisable = !authorICMJEForm?.file?.url;

  const submit = async () => {
    const { authorICMJEForm } = getValues();

    const formPayload = {
      authorICMJEForm: {
        ...authorICMJEForm,
        email: submittingAuthorField.value,
        userId: currentUser.id,
        isSubmittingAuthor: true
      }
    };
    const response =
      await submittingAuthorOnlyICMJESubmissionMutation.mutateAsync(
        formPayload
      );
    if (response.isSuccess) {
      toast({
        variant: 'success',
        title: 'Forms and Disclosures Concluded',
        description: (
          <span>
            You will receive a confirmation email with a copy of the form after
            submission.
          </span>
        ),
        isClosable: true
      });
      refetchSubmission();
    } else {
      toast({
        variant: 'error',
        title: 'Forms and Disclosures Conclusion Failed',
        description: 'Please try again later.'
      });
    }
  };

  const [isFileDownloading, setIsFileDownloading] = useState(false);

  const handleDownload = async (fileUrl: string) => {
    try {
      setIsFileDownloading(true); // disable button
      await downloadFile(fileUrl);
    } finally {
      setIsFileDownloading(false); // re-enable no matter what
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submit)}>
        <Flex flexDirection="column" gap={8}>
          <Box>
            <Flex flexDirection="column" gap={8}>
              <Flex flexDirection="column" gap={8}>
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingSection">Forms and Disclosures</Text>
                  <Text textStyle="textBody">
                    As the submitting author, please download and upload the
                    required form for this submission:
                  </Text>
                </Flex>
                <Banner
                  heading=""
                  content={
                    <Flex flexDirection="row">
                      <Text textStyle="headingItem">
                        Download The Forms and Disclosures First:{' '}
                      </Text>
                      <ButtonText
                        variant="primary"
                        onClick={() => {
                          if (config.authorICMJEFormUrl) {
                            handleDownload(config.authorICMJEFormUrl);
                          }
                        }}
                        isDisabled={isFileDownloading}
                      >
                        Download Forms and Disclosures
                      </ButtonText>
                    </Flex>
                  }
                  customIcon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                  variant="attention"
                />
              </Flex>

              {configs.AuthorICMJEFormFile && (
                <>
                  <FormFieldSingleFileUpload
                    submissionContextId={submissionContextId}
                    methods={methods}
                    config={configs.AuthorICMJEFormFile}
                  />
                </>
              )}

              <Flex width="100%" justifyContent="flex-start" gap={4}>
                <ProtectedAction
                  relation="author"
                  entityType="submission"
                  entityId={submissionId}
                >
                  <Button
                    variant="secondary"
                    type="submit"
                    disabled={isDisable || isSubmitting}
                    isLoading={isSubmitting}
                  >
                    Submit
                  </Button>
                </ProtectedAction>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </form>
    </FormProvider>
  );
};

export default SubmittingAuthorOnlyICMJEForm;
